/**
 * Access Code Modal Component
 * 
 * Modal dialog for confirming store import with access code.
 * 
 * @module AccessCodeModal
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { X, Lock } from 'lucide-react';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';

/**
 * Modal Overlay
 */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

/**
 * Modal Container
 */
const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

/**
 * Modal Header
 */
const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
`;

const ModalTitle = styled.h2`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${Spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${ThemeColors.text.secondary};
  transition: color 0.2s ease;

  &:hover {
    color: ${ThemeColors.text.primary};
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

/**
 * Modal Body
 */
const ModalBody = styled.div`
  padding: ${Spacing.lg};
`;

const ModalDescription = styled.p`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  margin-bottom: ${Spacing.lg};
  line-height: 1.6;
`;

const InputGroup = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  color: ${ThemeColors.text.primary};
  margin-bottom: ${Spacing.xs};
`;

const Input = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.main};
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;
  font-family: monospace;
  letter-spacing: 1px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }

  &.error {
    border-color: ${ThemeColors.error.main};
    
    &:focus {
      box-shadow: 0 0 0 3px ${ThemeColors.error.light};
    }
  }
`;

const ErrorMessage = styled.div`
  margin-top: ${Spacing.xs};
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.error.main};
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

/**
 * Modal Footer
 */
const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${Spacing.md};
  padding: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: ${Spacing.sm} ${Spacing.lg};
  border: none;
  border-radius: 8px;
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'primary' ? `
    background: ${ThemeColors.primary.main};
    color: white;

    &:hover:not(:disabled) {
      background: ${ThemeColors.primary.dark};
      transform: translateY(-1px);
    }
  ` : `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.main};

    &:hover:not(:disabled) {
      background: ${ThemeColors.background.secondary};
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

/**
 * Access Code Modal Props
 */
export interface AccessCodeModalProps {
  isOpen: boolean;
  onConfirm: (code: string) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * Access Code Modal Component
 */
export const AccessCodeModal: React.FC<AccessCodeModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  isLoading = false
}) => {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!accessCode.trim()) {
      setError('Please enter an access code');
      return;
    }

    onConfirm(accessCode.trim());
  };

  const handleCancel = () => {
    setAccessCode('');
    setError('');
    onCancel();
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) {
      handleConfirm();
    }
  };

  return (
    <ModalOverlay onClick={handleCancel}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <Lock size={20} />
            Confirm Import
          </ModalTitle>
          <CloseButton onClick={handleCancel} disabled={isLoading}>
            <X />
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <ModalDescription>
            Please enter the access code to confirm the store import. This action will add all stores to the database.
          </ModalDescription>

          <InputGroup>
            <Label htmlFor="accessCode">Access Code</Label>
            <Input
              id="accessCode"
              type="password"
              value={accessCode}
              onChange={(e) => {
                setAccessCode(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="Enter access code"
              className={error ? 'error' : ''}
              disabled={isLoading}
              autoFocus
            />
            {error && (
              <ErrorMessage>
                {error}
              </ErrorMessage>
            )}
          </InputGroup>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirm} disabled={isLoading}>
            {isLoading ? 'Confirming...' : 'Confirm'}
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  );
};

